import { useVirtualizer } from '@tanstack/react-virtual'
import { Button, Checkbox, Select, TextInput } from 'components/Form'
import { numberFormat } from 'components/helper'
import { Accordion, Modal, SimpleTable, Tabs, Tooltip } from 'components/Ui'
import InfoField from 'components/Ui/InfoField'
import React, { useEffect, useRef, useState } from 'react'
import { HiDownload, HiOutlineSearch } from 'react-icons/hi'
import { useSelector } from 'react-redux'
import { downloadCsvApi } from 'services/InvoiceService'
import { useWebWorker } from 'utils/hooks/useWebWorker'
import { flagOptions, invoiceStatusOptions } from 'utils/options'

const ReconcileView = ({
    data,
    invoiceData,
    reconciledData,
    versionOptions,
    version,
    fetchData,
}) => {
    const userInfo = useSelector((state) => state.auth.user)
    const [open, setOpen] = useState(false)
    // const [rowData, setRowData] = useState()
    const rowData = ''
    const [filter, setFilter] = useState(['1', '2', '3', '4'])
    const [search, setSearch] = useState('')
    const [selectedVersion, setSelectedVersion] = useState(version)
    const [expandedItems, setExpandedItems] = useState(new Set()) // Track expanded items
    const {
        data: processedData,
        loading,
        runWorker,
    } = useWebWorker('formatList')
    const {
        data: filteredData,
        loading: filterLoading,
        runWorker: filterRunWorker,
    } = useWebWorker('filterFormatList')
    const [invoicedata, setInvoicedata] = useState(null)
    const scrollEle = useRef(null)

    // Dynamic size estimation based on expanded state
    const virtualizer = useVirtualizer({
        count: invoicedata ? invoicedata[0].length : 0,
        getScrollElement: () => scrollEle.current,
        estimateSize: (index) => {
            // Return different sizes based on whether item is expanded
            const isExpanded = expandedItems.has(index)
            return isExpanded ? 600 : 80 // Collapsed: 80px, Expanded: 600px
        },
        overscan: 5, // Add some buffer items
    })

    useEffect(() => {
        if (filteredData && filter.length > 0) {
            setInvoicedata(filteredData)
        } else {
            setInvoicedata(processedData)
        }
        // Remeasure when data changes
        virtualizer.measure()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredData, processedData, filter])

    // Remeasure when expanded items change
    useEffect(() => {
        virtualizer.measure()
    }, [expandedItems, virtualizer])

    const virtualItems = virtualizer.getVirtualItems()

    useEffect(() => {
        if (data) {
            runWorker(data)
        }
    }, [runWorker, data])

    useEffect(() => {
        if (processedData) {
            filterRunWorker({ processedData, filter, search })
        }
    }, [filterRunWorker, processedData, filter, search])

    useEffect(() => {
        handleExpandAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoicedata])

    const resultFlag = (data) => {
        return data?.status === 'Reconciled'
            ? data?.total_difference_flag
                ? flagOptions[data.total_difference_flag]
                : 'matched'
            : 'notVerified'
    }

    const convertString = (string) => {
        if (typeof string === 'string') {
            let arr = string
                .toLowerCase()
                .split('_')
                .map((item) => item.charAt(0).toUpperCase() + item.substring(1))
            return arr.join(' ')
        }
    }

    const RenderList = (value) => {
        if (value === null) return <div>null</div>
        if (Array.isArray(value)) {
            return value.map((item) => {
                return Object.keys(item).map((key) => {
                    return (
                        <div className="min-w-max" key={key}>
                            <p>
                                <b>{convertString(key)}</b> : {item[key]}
                            </p>
                        </div>
                    )
                })
            })
        } else {
            return Object.keys(value).map((key) => {
                return (
                    <div className="min-w-max" key={key}>
                        <p>
                            <b>{convertString(key)} :</b>
                            {numberFormat(value[key], userInfo.currency_code)}
                        </p>
                    </div>
                )
            })
        }
    }

    const handleSearch = (value) => {
        setSearch(value)
    }

    const formulaReplace = (formula, data) => {
        return formula.replace(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g, (match) => {
            return data.hasOwnProperty(match) ? data[match] : match
        })
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'matched':
                return 'text-green'
            case 'overcharged':
                return 'text-red'
            case 'undercharged':
                return 'text-yellow'
            case 'notVerified':
                return 'text-gray3'
            default:
                return 'text-black dark:text-white'
        }
    }

    // Handle accordion toggle
    const handleAccordionToggle = (index) => {
        const newExpandedItems = new Set(expandedItems)
        if (expandedItems.has(index)) {
            newExpandedItems.delete(index)
        } else {
            newExpandedItems.add(index)
        }
        setExpandedItems(newExpandedItems)
    }

    // Expand all items
    const handleExpandAll = () => {
        if (invoicedata && invoicedata[0]) {
            const allIndices = new Set(
                Array.from({ length: invoicedata[0].length }, (_, i) => i),
            )
            setExpandedItems(allIndices)
        }
    }

    // Collapse all items
    const handleCollapseAll = () => {
        setExpandedItems(new Set())
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Result
                </h2>
            </div>
            <div>
                <div className="card">
                    <div className="p-4 bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-9 mb-5">
                        <div className="flex flex-col justify-between items-center gap-4 2xl:flex-row">
                            <div className="flex gap-2">
                                <Checkbox
                                    id={'1'}
                                    name={'1'}
                                    checked={filter?.includes('1')}
                                    className={`ml-2 rtl:mr-2 capitalize font-semibold text-green`}
                                    onChange={(e) => {
                                        const { name } = e.target

                                        let arr = [...filter]
                                        if (arr.includes(name)) {
                                            if (arr.length > 1) {
                                                arr = arr.filter(
                                                    (item) => item !== name,
                                                )
                                            }
                                        } else {
                                            arr.push(name)
                                        }
                                        setFilter(arr)
                                    }}
                                    label={'Matched'}
                                />
                                <Checkbox
                                    id={'2'}
                                    name={'2'}
                                    checked={filter?.includes('2')}
                                    className={`ml-2 rtl:mr-2 capitalize font-semibold text-red`}
                                    onChange={(e) => {
                                        const { name } = e.target
                                        let arr = [...filter]
                                        if (arr.includes(name)) {
                                            if (arr.length > 1) {
                                                arr = arr.filter(
                                                    (item) => item !== name,
                                                )
                                            }
                                        } else {
                                            arr.push(name)
                                        }
                                        setFilter(arr)
                                    }}
                                    label={'Overcharged'}
                                />
                                <Checkbox
                                    id={'3'}
                                    name={'3'}
                                    checked={filter?.includes('3')}
                                    className={`ml-2 rtl:mr-2 capitalize font-semibold text-yellow`}
                                    onChange={(e) => {
                                        const { name } = e.target
                                        let arr = [...filter]
                                        if (arr.includes(name)) {
                                            if (arr.length > 1) {
                                                arr = arr.filter(
                                                    (item) => item !== name,
                                                )
                                            }
                                        } else {
                                            arr.push(name)
                                        }
                                        setFilter(arr)
                                    }}
                                    label={'Undercharged'}
                                />
                                <Checkbox
                                    id={'4'}
                                    name={'4'}
                                    checked={filter?.includes('4')}
                                    className={`ml-2 rtl:mr-2 capitalize font-semibold text-gray3`}
                                    onChange={(e) => {
                                        const { name } = e.target
                                        let arr = [...filter]
                                        if (arr.includes(name)) {
                                            if (arr.length > 1) {
                                                arr = arr.filter(
                                                    (item) => item !== name,
                                                )
                                            }
                                        } else {
                                            arr.push(name)
                                        }
                                        setFilter(arr)
                                    }}
                                    label={'Not Verified'}
                                />
                            </div>
                            <div className="items-center gap-4 md:flex">
                                <div className="flex gap-2 mb-2 md:mb-0">
                                    <Button
                                        size="sm"
                                        className="text-primary border border-primary dark:text-secondary dark:border-secondary"
                                        onClick={handleExpandAll}
                                    >
                                        Expand All
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="text-gray-600 border border-gray-600 dark:text-gray-400 dark:border-gray-400"
                                        onClick={handleCollapseAll}
                                    >
                                        Collapse All
                                    </Button>
                                </div>
                                <TextInput
                                    id="search"
                                    name="search"
                                    className="w-full sm:w-auto"
                                    placeholder="Search consignment no."
                                    icon={
                                        <HiOutlineSearch className="text-lg" />
                                    }
                                    onChange={(e) =>
                                        handleSearch(e.target.value)
                                    }
                                />
                                <Select
                                    id="version"
                                    name="version"
                                    className="min-w-28"
                                    options={versionOptions}
                                    placeholder="Select Version"
                                    value={selectedVersion}
                                    onChange={(e) => {
                                        setSelectedVersion(e.target.value)
                                        fetchData(e.target.value)
                                    }}
                                />
                                <Tooltip
                                    title={'Download Csv'}
                                    placement={'top'}
                                >
                                    <Button
                                        className="text-primary border border-primary dark:text-secondary dark:border-secondary"
                                        onClick={() => {
                                            downloadCsvApi(
                                                reconciledData?.invoice_id,
                                                filter,
                                            )
                                                .then((response) => {
                                                    if (response.data) {
                                                        const blob = new Blob(
                                                            [response.data],
                                                            {
                                                                type: 'text/csv',
                                                            },
                                                        )
                                                        const url =
                                                            URL.createObjectURL(
                                                                blob,
                                                            )
                                                        const link =
                                                            document.createElement(
                                                                'a',
                                                            )
                                                        link.href = url
                                                        const nameString =
                                                            filter
                                                                .map(
                                                                    (item) =>
                                                                        invoiceStatusOptions[
                                                                            item
                                                                        ],
                                                                )
                                                                .join('_')
                                                        link.download = `${invoiceData?.invoice_no}_${nameString}.csv`
                                                        link.click()
                                                        URL.revokeObjectURL(url)
                                                    }
                                                })
                                                .catch((error) => error)
                                        }}
                                    >
                                        <HiDownload size={20} /> {'Download'}
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-9 mb-5">
                        <div className="px-8 py-6 flex justify-between items-start">
                            <div className="flex-1">
                                <h1 className="text-black text-4xl font-bold mb-2">
                                    Invoice #{invoiceData?.invoice_no}
                                </h1>
                                <p className="text-lg mb-1">
                                    from {invoiceData?.vendor_name}
                                </p>
                                <p className="text-base">
                                    Client: {invoiceData?.client_name} | Date:{' '}
                                    {invoiceData?.invoice_date}
                                </p>
                            </div>

                            {/* Progress Circle */}
                            <div className="flex flex-col items-center">
                                <p className="text-sm mb-3 uppercase tracking-wider">
                                    RECONCILIATION PROGRESS
                                </p>
                                <div className="relative w-24 h-24">
                                    <svg
                                        className="w-24 h-24 transform -rotate-90"
                                        viewBox="0 0 36 36"
                                    >
                                        <path
                                            className="text-slate-600"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            fill="none"
                                            d="M18 2.0845
                   a 15.9155 15.9155 0 0 1 0 31.831
                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                        />
                                        <path
                                            className="text-info"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeDasharray={`${parseInt(
                                                (reconciledData?.total_verified_line_items /
                                                    invoiceData?.total_items) *
                                                    100,
                                            )}, 100`}
                                            strokeLinecap="round"
                                            fill="none"
                                            d="M18 2.0845
                   a 15.9155 15.9155 0 0 1 0 31.831
                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-black text-2xl font-bold">
                                            {parseInt(
                                                (reconciledData?.total_verified_line_items /
                                                    invoiceData?.total_items) *
                                                    100,
                                            )}
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-slate-600 mx-8"></div>
                        <div className="px-8 py-6 grid grid-cols-4 gap-8">
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    INVOICE AMOUNT
                                </p>
                                <p className="text-black text-2xl font-bold">
                                    {numberFormat(
                                        invoiceData?.total_amount,
                                        'AUD',
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    INVOICE ITEMS
                                </p>
                                <p className="text-black text-2xl font-bold">
                                    {invoiceData?.total_items}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    VERIFIED AMOUNT
                                </p>
                                <p className="text-black text-2xl font-bold">
                                    {numberFormat(
                                        reconciledData?.reconciled_invoice_total,
                                        'AUD',
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    VERIFIED ITEMS
                                </p>
                                <p className="text-black text-2xl font-bold">
                                    {reconciledData?.total_verified_line_items}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    OVERCHARGED
                                </p>
                                <p className="text-red text-2xl font-bold">
                                    {numberFormat(
                                        reconciledData?.total_overcharged,
                                        'AUD',
                                    )}{' '}
                                    (
                                    {
                                        reconciledData?.total_overcharged_line_items
                                    }
                                    )
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    UNDERCHARGED
                                </p>
                                <p className="text-yellow text-2xl font-bold">
                                    {numberFormat(
                                        reconciledData?.total_undercharged,
                                        'AUD',
                                    )}{' '}
                                    (
                                    {
                                        reconciledData?.total_undercharged_line_items
                                    }
                                    )
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    CALC. VERIFIED AMT
                                </p>
                                <p className="text-black text-2xl font-bold">
                                    {numberFormat(
                                        reconciledData?.calculated_final_total,
                                        'AUD',
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm uppercase tracking-wide mb-2">
                                    NET DIFFERENCE
                                </p>
                                <p className="text-black text-2xl font-bold">
                                    {numberFormat(
                                        reconciledData?.reconciled_total_difference,
                                        'AUD',
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        ref={scrollEle}
                        className="overflow-auto h-[50dvh] w-full"
                    >
                        <div>
                            <div
                                className="relative"
                                style={{
                                    height: `${virtualizer.getTotalSize()}px`,
                                }}
                            >
                                {loading || filterLoading || !invoicedata ? (
                                    <div>Loading...</div>
                                ) : (
                                    <div
                                        className="absolute w-full"
                                        style={{
                                            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                                        }}
                                    >
                                        <Accordion.Group version={1}>
                                            <Accordion>
                                                {virtualItems?.map(
                                                    ({ index, key }) => {
                                                        const value =
                                                            invoicedata[0][
                                                                index
                                                            ]
                                                        const reconcileData =
                                                            invoicedata[1][
                                                                value?.unique_id
                                                            ]
                                                        const isExpanded =
                                                            expandedItems.has(
                                                                index,
                                                            )

                                                        return (
                                                            <Accordion.Item
                                                                key={`card-${index}`}
                                                                id={`card-${index}`}
                                                            >
                                                                <Accordion.Header
                                                                    status={resultFlag(
                                                                        reconcileData,
                                                                    )}
                                                                    onClick={() =>
                                                                        handleAccordionToggle(
                                                                            index,
                                                                        )
                                                                    }
                                                                >
                                                                    <Accordion.Title
                                                                        status={
                                                                            resultFlag(
                                                                                reconcileData,
                                                                            ) ===
                                                                            'red'
                                                                                ? 'overcharged'
                                                                                : resultFlag(
                                                                                        reconcileData,
                                                                                    ) ===
                                                                                    'yellow'
                                                                                  ? 'undercharged'
                                                                                  : resultFlag(
                                                                                          reconcileData,
                                                                                      ) ===
                                                                                      'green'
                                                                                    ? 'matched'
                                                                                    : resultFlag(
                                                                                          reconcileData,
                                                                                      )
                                                                        }
                                                                    >
                                                                        <div>
                                                                            <div className="flex items-center justify-between">
                                                                                <span>
                                                                                    {value?.con_note
                                                                                        ? `Consignment No: ${value.con_note}`
                                                                                        : `Job No: ${value.job_number}`}
                                                                                </span>
                                                                            </div>
                                                                            <p className="text-sm text-black/70 dark:text-white/70 mt-1">
                                                                                {`Service: ${value.service} | ${value?.from || value?.from_location} to ${value?.to || value?.to_location}`}
                                                                            </p>
                                                                        </div>
                                                                    </Accordion.Title>
                                                                    <div className="flex items-center space-x-4">
                                                                        <span
                                                                            className={`text-lg font-bold ${getStatusColor(
                                                                                resultFlag(
                                                                                    reconcileData,
                                                                                ) ===
                                                                                    'red'
                                                                                    ? 'overcharged'
                                                                                    : resultFlag(
                                                                                            reconcileData,
                                                                                        ) ===
                                                                                        'yellow'
                                                                                      ? 'undercharged'
                                                                                      : resultFlag(
                                                                                              reconcileData,
                                                                                          ) ===
                                                                                          'green'
                                                                                        ? 'matched'
                                                                                        : resultFlag(
                                                                                              reconcileData,
                                                                                          ),
                                                                            )} ml-4`}
                                                                        >
                                                                            {numberFormat(
                                                                                reconcileData?.difference ||
                                                                                    0,
                                                                                'AUD',
                                                                            )}
                                                                        </span>
                                                                        <Accordion.Badge
                                                                            status={
                                                                                resultFlag(
                                                                                    reconcileData,
                                                                                ) ===
                                                                                'red'
                                                                                    ? 'overcharged'
                                                                                    : resultFlag(
                                                                                            reconcileData,
                                                                                        ) ===
                                                                                        'yellow'
                                                                                      ? 'undercharged'
                                                                                      : resultFlag(
                                                                                              reconcileData,
                                                                                          ) ===
                                                                                          'green'
                                                                                        ? 'matched'
                                                                                        : resultFlag(
                                                                                              reconcileData,
                                                                                          )
                                                                            }
                                                                        />
                                                                        <Accordion.Toggle
                                                                            isExpanded={
                                                                                isExpanded
                                                                            }
                                                                        />
                                                                    </div>
                                                                </Accordion.Header>

                                                                <Accordion.Content
                                                                    isExpanded={
                                                                        isExpanded
                                                                    }
                                                                >
                                                                    <div className="grid grid-cols-1 gap-28 p-2 md:grid-cols-2">
                                                                        <div
                                                                            className={`space-y-4 p-4 bg-green rounded-md`}
                                                                        >
                                                                            <div>
                                                                                <p className="text-sm font-bold text-white/70 uppercase tracking-wide mb-3">
                                                                                    Invoice
                                                                                    Value
                                                                                </p>
                                                                                <div className="space-y-2">
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            Type:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {
                                                                                                value.type
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            Charge
                                                                                            Weight:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {
                                                                                                value.charge_weight
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            Cost:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {
                                                                                                value.cost
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            Fuel
                                                                                            Levy:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {
                                                                                                value.fuel_levy
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            Items/Qty:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {
                                                                                                value.qty
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            NET:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {
                                                                                                value.net
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            Total:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold text-white">
                                                                                            {numberFormat(
                                                                                                value?.total,
                                                                                                'AUD',
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div
                                                                            className={`space-y-4 p-4 ${
                                                                                resultFlag(
                                                                                    reconcileData,
                                                                                ) ===
                                                                                'red'
                                                                                    ? 'bg-red text-white'
                                                                                    : resultFlag(
                                                                                            reconcileData,
                                                                                        ) ===
                                                                                        'yellow'
                                                                                      ? 'bg-yellow text-black'
                                                                                      : resultFlag(
                                                                                              reconcileData,
                                                                                          ) ===
                                                                                          'green'
                                                                                        ? 'bg-green text-white'
                                                                                        : 'bg-gray3 text-white'
                                                                            } rounded-md`}
                                                                        >
                                                                            <div>
                                                                                <p
                                                                                    className={`text-sm font-bold ${resultFlag(reconcileData) === 'yellow' ? 'text-black/70' : 'text-white/70'} uppercase tracking-wide mb-3`}
                                                                                >
                                                                                    Reconciled
                                                                                    Value
                                                                                </p>
                                                                                <div className="space-y-2">
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Type:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {
                                                                                                reconcileData.type
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Charge
                                                                                            Weight:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {
                                                                                                reconcileData.invoiced_charge_weight
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Cost
                                                                                            Difference:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {
                                                                                                reconcileData.cost_difference
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Fuel
                                                                                            Levy
                                                                                            Difference:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {
                                                                                                reconcileData.fuel_levy_difference
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Items/Qty:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {
                                                                                                reconcileData.qty
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            NET:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {
                                                                                                reconcileData.net
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Total:
                                                                                        </span>
                                                                                        <span className="text-sm font-bold">
                                                                                            {numberFormat(
                                                                                                reconcileData?.calculated_total ||
                                                                                                    reconcileData?.invoiced_total,
                                                                                                'AUD',
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                                                        <span className="text-sm font-bold">
                                                                                            Difference:
                                                                                        </span>
                                                                                        <span
                                                                                            className={`text-sm font-bold`}
                                                                                        >
                                                                                            {numberFormat(
                                                                                                reconcileData?.difference,
                                                                                                'AUD',
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Accordion.Content>
                                                            </Accordion.Item>
                                                        )
                                                    },
                                                )}
                                            </Accordion>
                                        </Accordion.Group>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <Modal
                    show={open}
                    onClose={() => {
                        setOpen(false)
                    }}
                >
                    <div>
                        <Tabs.Group version={1}>
                            <Tabs.List>
                                <Tabs value="1">Details</Tabs>
                                {rowData?.data?.formula && (
                                    <Tabs value="2">Calculations</Tabs>
                                )}
                            </Tabs.List>
                            <div className="p-4">
                                <Tabs.Panels className="mt-4">
                                    <div className="grid grid-cols-1 gap-4 max-h-[400px] overflow-y-auto">
                                        <InfoField
                                            title="Service Name"
                                            value={rowData?.name}
                                        />
                                        {rowData?.data &&
                                            Object.keys(rowData?.data).map(
                                                (key) => {
                                                    if (key === 'service')
                                                        return null
                                                    return typeof rowData?.data[
                                                        key
                                                    ] !== 'object' ? (
                                                        !key.includes('flag') &&
                                                        rowData?.data[key] !==
                                                            'NA' &&
                                                        ![
                                                            'formula',
                                                            'fuel_levy_formula',
                                                            'fuel_levy percentage',
                                                        ].includes(
                                                            key.toLowerCase(),
                                                        ) ? (
                                                            <InfoField
                                                                key={key}
                                                                title={convertString(
                                                                    key,
                                                                )}
                                                                value={
                                                                    [
                                                                        'cost',
                                                                        'cost_difference',
                                                                        'calculated_cost',
                                                                        'invoiced_cost',
                                                                        'other_charge_amount',
                                                                        'net',
                                                                        'gst',
                                                                        'total',
                                                                        'other_charges',
                                                                        'invoiced_total',
                                                                        'calculated_total',
                                                                        'difference',
                                                                    ].includes(
                                                                        key.toLowerCase(),
                                                                    )
                                                                        ? numberFormat(
                                                                              rowData
                                                                                  ?.data[
                                                                                  key
                                                                              ],
                                                                              userInfo.currency_code,
                                                                          )
                                                                        : key ===
                                                                            'status'
                                                                          ? rowData
                                                                                ?.data[
                                                                                key
                                                                            ] ===
                                                                            'Reconciled'
                                                                              ? 'Verified'
                                                                              : 'Not Verified'
                                                                          : rowData
                                                                                ?.data[
                                                                                key
                                                                            ]
                                                                }
                                                            />
                                                        ) : null
                                                    ) : (
                                                        <div
                                                            key={key}
                                                            className="grid grid-cols-2 gap-4"
                                                        >
                                                            <span className="font-semibold text-gray-700 dark:text-gray-200">
                                                                {convertString(
                                                                    key,
                                                                )}
                                                            </span>
                                                            <div className="grid grid-cols-1 gap-4">
                                                                {RenderList(
                                                                    rowData
                                                                        ?.data[
                                                                        key
                                                                    ],
                                                                )}
                                                            </div>
                                                        </div>
                                                    )
                                                },
                                            )}
                                    </div>
                                </Tabs.Panels>
                                <Tabs.Panels className="mt-4">
                                    <div>
                                        <SimpleTable>
                                            <thead>
                                                <SimpleTable.TR>
                                                    <SimpleTable.TH>
                                                        <div className="text-center text-white">
                                                            Name
                                                        </div>
                                                    </SimpleTable.TH>
                                                    <SimpleTable.TH>
                                                        <div className="text-center text-white">
                                                            Formula
                                                        </div>
                                                    </SimpleTable.TH>
                                                    <SimpleTable.TH>
                                                        <div className="text-center text-white">
                                                            Formula With Value
                                                        </div>
                                                    </SimpleTable.TH>
                                                    <SimpleTable.TH>
                                                        <div className="text-center text-white">
                                                            Value
                                                        </div>
                                                    </SimpleTable.TH>
                                                </SimpleTable.TR>
                                            </thead>
                                            <tbody>
                                                {rowData?.data?.formula ? (
                                                    <SimpleTable.TR>
                                                        <SimpleTable.TD>
                                                            Formula
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {
                                                                rowData?.data
                                                                    ?.formula
                                                            }
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {rowData?.data
                                                                ?.formula ===
                                                            'slab_rate'
                                                                ? rowData?.data
                                                                      ?.rate
                                                                : formulaReplace(
                                                                      rowData
                                                                          ?.data
                                                                          ?.formula,
                                                                      {
                                                                          ...rowData?.data,
                                                                          charge_weight:
                                                                              rowData
                                                                                  ?.data
                                                                                  .calculated_charge_weight,
                                                                      },
                                                                  )}
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {rowData?.data
                                                                ?.formula ===
                                                            'slab_rate'
                                                                ? rowData?.data
                                                                      ?.rate
                                                                : parseFloat(
                                                                      // eslint-disable-next-line
                                                                      eval(
                                                                          formulaReplace(
                                                                              rowData
                                                                                  ?.data
                                                                                  ?.formula,
                                                                              {
                                                                                  ...rowData?.data,
                                                                                  charge_weight:
                                                                                      rowData
                                                                                          ?.data
                                                                                          .calculated_charge_weight,
                                                                              },
                                                                          ),
                                                                      ),
                                                                  ).toFixed(2)}
                                                        </SimpleTable.TD>
                                                    </SimpleTable.TR>
                                                ) : null}
                                                {rowData?.data
                                                    ?.fuel_levy_formula ? (
                                                    <SimpleTable.TR>
                                                        <SimpleTable.TD>
                                                            Fuel Levy Formula
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {
                                                                rowData?.data
                                                                    ?.fuel_levy_formula
                                                            }
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {formulaReplace(
                                                                rowData?.data
                                                                    ?.fuel_levy_formula,
                                                                {
                                                                    ...rowData?.data,
                                                                    fuel_levy:
                                                                        parseFloat(
                                                                            rowData
                                                                                ?.data[
                                                                                'fuel_levy_percentage'
                                                                            ] /
                                                                                100,
                                                                        ),
                                                                },
                                                            )}
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {
                                                                rowData?.data
                                                                    ?.calculated_fuel_levy
                                                            }
                                                        </SimpleTable.TD>
                                                    </SimpleTable.TR>
                                                ) : null}
                                                {rowData?.data
                                                    ?.fuel_levy_formula ? (
                                                    <SimpleTable.TR>
                                                        <SimpleTable.TD>
                                                            Fuel Levy Percentage
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {
                                                                rowData?.data[
                                                                    'fuel_levy_percentage'
                                                                ]
                                                            }
                                                            %
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {
                                                                rowData?.data[
                                                                    'fuel_levy_percentage'
                                                                ]
                                                            }{' '}
                                                            / 100
                                                        </SimpleTable.TD>
                                                        <SimpleTable.TD>
                                                            {parseFloat(
                                                                rowData?.data[
                                                                    'fuel_levy_percentage'
                                                                ] / 100,
                                                            )?.toFixed(2)}
                                                        </SimpleTable.TD>
                                                    </SimpleTable.TR>
                                                ) : null}
                                            </tbody>
                                        </SimpleTable>
                                    </div>
                                </Tabs.Panels>
                            </div>
                        </Tabs.Group>
                    </div>
                </Modal>
            </div>
        </>
    )
}

export default ReconcileView
